import {
  Conversation,
  Message,
  ParticipantConversation,
  RoleConversation,
  TypeConversation,
} from "../types/models/conversation";

// Habillage d'un fil pour l'affichage. Le backend ne renvoie que sa portée
// (type + cible) et ses participants : le libellé se construit ici, en fonction
// de qui regarde — un gestionnaire voit « Course #42 · client », le client voit
// « Ma course #42 ».
//
// Fonctions utilitaires appelées hors du rendu React (pas de hook possible ici) :
// les traductions sont résolues par l'appelant via `useTranslations` et passées
// en argument, comme pour `notification-course-labels`.

/** Libellés traduits partagés par les fonctions de ce fichier. */
export type ConversationLabels = {
  roles: Record<RoleConversation, string>;
  roleFallback: string;
  deletedAccount: string;
  dispatch: string;
  courseTitle: (id: number | null) => string;
  courseTitleClient: (id: number | null) => string;
  courseTitleDriver: (id: number | null) => string;
  courseTitleWithDispatch: (id: number | null) => string;
  supportWith: (nom: string) => string;
  supportDefault: string;
  noMessages: string;
  youPrefix: string;
  justNow: string;
  today: string;
  yesterday: string;
};

export const libelleRole = (role: RoleConversation, labels: ConversationLabels) =>
  labels.roles[role] ?? labels.roleFallback;

export const nomParticipant = (
  participant: ParticipantConversation,
  labels: ConversationLabels,
) =>
  `${participant.prenom ?? ""} ${participant.nom ?? ""}`.trim() ||
  labels.deletedAccount;

export const nomAuteur = (message: Message, labels: ConversationLabels) =>
  `${message.auteurPrenom ?? ""} ${message.auteurNom ?? ""}`.trim() ||
  labels.deletedAccount;

/** Icône du fil, dans le registre de `shared/ui/icon`. */
export const iconeConversation = (conversation: Conversation) => {
  switch (conversation.type) {
    case TypeConversation.CourseChauffeur:
      return "local_taxi";
    case TypeConversation.SupportChauffeur:
      return "support";
    default:
      return "chat";
  }
};

/**
 * Titre du fil. `estBackOffice` distingue la file de traitement du back-office,
 * où il faut lire d'un coup d'œil de quelle course et de quel côté il s'agit,
 * de la vue d'un client, pour qui il n'y a qu'une conversation par course.
 */
export const titreConversation = (
  conversation: Conversation,
  labels: ConversationLabels,
  estBackOffice = false,
) => {
  switch (conversation.type) {
    case TypeConversation.CourseClient:
      return estBackOffice
        ? labels.courseTitleClient(conversation.idCourse)
        : labels.courseTitle(conversation.idCourse);

    case TypeConversation.CourseChauffeur:
      return estBackOffice
        ? labels.courseTitleDriver(conversation.idCourse)
        : labels.courseTitleWithDispatch(conversation.idCourse);

    case TypeConversation.SupportChauffeur: {
      const chauffeur = conversation.participants.find(
        (participant) => participant.role === RoleConversation.Chauffeur,
      );

      return estBackOffice && chauffeur
        ? labels.supportWith(nomParticipant(chauffeur, labels))
        : labels.supportDefault;
    }

    default:
      return conversation.cle;
  }
};

/**
 * Sous-titre : les autres participants nommés. Les gestionnaires y sont réduits
 * à « la centrale » — ils ne sont pas des interlocuteurs nominatifs, leur accès
 * venant de leur rôle et non d'une inscription au fil.
 */
export const sousTitreConversation = (
  conversation: Conversation,
  idContact: number | null,
  labels: ConversationLabels,
) => {
  const autres = conversation.participants.filter(
    (participant) =>
      participant.idContact !== idContact &&
      participant.role !== RoleConversation.Gestionnaire,
  );

  const noms = autres.map((participant) => nomParticipant(participant, labels));

  const avecCentrale =
    conversation.type !== TypeConversation.CourseClient ||
    conversation.participants.some(
      (participant) => participant.role === RoleConversation.Gestionnaire,
    );

  if (avecCentrale) noms.push(labels.dispatch);

  return noms.join(", ");
};

/** Aperçu du dernier message dans la boîte de réception. */
export const apercuDernierMessage = (
  conversation: Conversation,
  idContact: number | null,
  labels: ConversationLabels,
) => {
  const dernier = conversation.dernierMessage;
  if (!dernier) return labels.noMessages;

  const prefixe =
    dernier.idAuteurContact === idContact ? `${labels.youPrefix} ` : "";

  return `${prefixe}${dernier.contenu}`;
};

/** Logique de découpage des jours partagée avec l'app mobile ; seuls les libellés viennent d'ici. */
export { horodatageCourt, libelleJour } from "@onclicks/sharedmobilecommon/utils/conversation-datetime";
